/**
 * The target table (2.J deliverable 15): a `grid` of secrets or pages, one checkbox a row. Arrow
 * keys, Home, End and Page keys move between cells with 1.N-2's `nextCell`; Space toggles the row
 * of the focused cell. Rows past the first 200 shown come with "Show more".
 */
import { Button, Checkbox } from '@codex/ui';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { type GridKey, type GridPos, isGridKey, nextCell } from '../../catalogue/model/grid-nav.js';
import type { Selection, TargetRow } from '../model/targets.js';

export const WINDOW = 200;
const COLUMNS = ['Select', 'Title', 'Detail', 'State'] as const;

export function TargetTable({
  rows,
  selection,
  onToggle,
  detailLabel,
}: {
  rows: readonly TargetRow[];
  selection: Selection;
  onToggle: (key: string) => void;
  detailLabel: string;
}) {
  const [limit, setLimit] = useState(WINDOW);
  const [pos, setPos] = useState<GridPos>({ row: 1, col: 0 });
  const grid = useRef<HTMLTableElement>(null);
  const drawn = rows.slice(0, limit);
  const at = { row: Math.min(pos.row, drawn.length), col: pos.col };

  // The grid has one Tab stop (the roving cell); Space on a cell toggles its row's checkbox, so
  // the checkboxes themselves are not Tab stops (the Checkbox primitive takes no tabIndex).
  useEffect(() => {
    for (const box of grid.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ??
      [])
      box.tabIndex = -1;
  });

  const focusCell = (next: GridPos) => {
    setPos(next);
    grid.current?.querySelector<HTMLElement>(`[data-pos="${next.row},${next.col}"]`)?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-pos]');
    if (target === null) return;
    const [row, col] = (target.dataset.pos ?? '0,0').split(',').map(Number) as [number, number];
    if (event.key === ' ' && row > 0) {
      event.preventDefault();
      const picked = drawn[row - 1];
      if (picked !== undefined) onToggle(picked.key);
      return;
    }
    if (!isGridKey(event.key)) return;
    event.preventDefault();
    const move = nextCell(
      { row, col },
      event.key as GridKey,
      { rows: drawn.length, cols: COLUMNS.length, total: drawn.length, hasMore: false },
      event.ctrlKey || event.metaKey,
    );
    if (move.kind === 'move') focusCell(move.pos);
  };
  const cell = (row: number, col: number) => ({
    'data-pos': `${row},${col}`,
    tabIndex: at.row === row && at.col === col ? 0 : -1,
    onFocus: () => setPos({ row, col }),
  });

  return (
    <div className="cx-bootstrap__grid-wrap">
      <table
        ref={grid}
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: a selectable grid
        role="grid"
        aria-label="Choose what to mark"
        aria-multiselectable="true"
        aria-rowcount={rows.length + 1}
        className="cx-bootstrap__grid"
        onKeyDown={onKeyDown}
      >
        <thead>
          <tr>
            {COLUMNS.map((name, col) => (
              <th key={name} scope="col" {...cell(0, col)}>
                {name === 'Detail' ? detailLabel : name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {drawn.map((row, i) => {
            const selected = selection.has(row.key);
            return (
              <tr key={row.key} aria-selected={selected} aria-rowindex={i + 2}>
                <td {...cell(i + 1, 0)}>
                  <Checkbox
                    label={<span className="cx-visually-hidden">{`Choose ${row.title}`}</span>}
                    checked={selected}
                    onChange={() => onToggle(row.key)}
                  />
                </td>
                <td {...cell(i + 1, 1)}>{row.title}</td>
                <td {...cell(i + 1, 2)}>{row.detail}</td>
                <td {...cell(i + 1, 3)}>{row.state}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > drawn.length && (
        <Button size="sm" onClick={() => setLimit(limit + WINDOW)}>
          {`Show more (${rows.length - drawn.length} left)`}
        </Button>
      )}
    </div>
  );
}
